import { resoudreChemin } from './acheminement'
import { ajouterJours } from './calendrier'
import type { Rubrique, RubriqueDrive, TypeEntite, TypePiece } from './domaine'

// Rubriques reconnues à la lecture d'une pièce, comme dans un dossier de révision : un dossier
// permanent (ce qui vaut d'un exercice à l'autre : statuts, baux, prêts, actes) et un dossier
// par exercice, découpé par cycle ; le foyer a ses propres rubriques, par année civile. La
// rubrique guide le placement et sert aux anciens chemins réglés à la main ; la place d'une
// pièce est un dossier du plan de l'entité (plan.ts), dont le plan de départ reprend celui-ci.

export type PlanClassement = 'societe' | 'foyer'

export type Place = { permanent: boolean; dossier: string }

const permanent = (dossier: string): Place => ({ permanent: true, dossier })
const annuel = (dossier: string): Place => ({ permanent: false, dossier })

/** Où se range chaque rubrique ; absente du plan, elle n'a pas de place dans cette entité. */
export const PLANS: Record<PlanClassement, Partial<Record<Rubrique, Place>>> = {
  societe: {
    juridique: permanent('1.1 - Juridique'),
    contrats: permanent('1.2 - Contrats et assurances'),
    baux: permanent('1.3 - Baux'),
    financements: permanent('1.4 - Emprunts et financements'),
    patrimoine: permanent('1.5 - Patrimoine'),
    ventes: annuel('01 - Ventes et loyers'),
    achats: annuel('02 - Achats et frais'),
    banque: annuel('03 - Banque'),
    fiscal: annuel('04 - Fiscal'),
    social: annuel('05 - Social'),
    travaux: annuel('06 - Travaux et immobilisations'),
    courriers: annuel('07 - Courriers et litiges'),
    cloture: annuel('08 - Clôture'),
  },
  foyer: {
    etat_civil: permanent('1.1 - État civil et identité'),
    contrats: permanent('1.2 - Contrats et assurances'),
    baux: permanent('1.2 - Contrats et assurances'),
    financements: permanent('1.3 - Emprunts'),
    patrimoine: permanent('1.4 - Patrimoine'),
    fiscal: annuel('01 - Impôts'),
    revenus: annuel('02 - Revenus'),
    sante: annuel('03 - Santé'),
    famille: annuel('04 - Famille'),
    // Pajemploi, garde d'enfants : le foyer employeur.
    social: annuel('04 - Famille'),
    logement: annuel('05 - Logement'),
    travaux: annuel('05 - Logement'),
    banque: annuel('06 - Banque et placements'),
    achats: annuel('07 - Achats et garanties'),
    courriers: annuel('08 - Courriers'),
  },
}

export function planDe(type: TypeEntite): PlanClassement {
  return type === 'foyer' ? 'foyer' : 'societe'
}

export function rubriqueAdmise(plan: PlanClassement, rubrique: Rubrique): boolean {
  return PLANS[plan][rubrique] !== undefined
}

/**
 * Rubrique quand la lecture n'en donne pas (XML structuré, lecture antérieure au plan) : ce
 * que le type dit sans ambiguïté, sinon null (un humain choisit).
 */
export function rubriqueParDefaut(p: { type: TypePiece; emise: boolean }): Rubrique | null {
  switch (p.type) {
    case 'facture':
      return p.emise ? 'ventes' : 'achats'
    case 'releve':
      return 'banque'
    case 'avis':
    case 'accuse_depot':
      return 'fiscal'
    case 'fiche_paie':
      return 'social'
    default:
      return null
  }
}

/**
 * Rubrique retenue au rattachement. Une facture émise est une vente, quoi qu'en dise la
 * lecture ; une facture reçue lue « ventes » est suspecte (null : un humain choisit). Une
 * lecture qui hésite (null) reste dans le doute ; sans avis de la lecture, le type décide.
 */
export function rubriqueDe(
  x: { type: TypePiece; rubrique?: Rubrique | null },
  emise: boolean,
): Rubrique | null {
  if (x.type === 'facture' && emise) return 'ventes'
  if (x.rubrique === undefined) return rubriqueParDefaut({ type: x.type, emise })
  if (x.type === 'facture' && x.rubrique === 'ventes') return null
  return x.rubrique
}

/** Ancien dossier (chemins réglés à la main) correspondant à une rubrique du plan. */
export function versRubriqueDrive(rubrique: Rubrique): RubriqueDrive {
  if (rubrique === 'ventes') return 'ventes'
  if (rubrique === 'achats' || rubrique === 'travaux') return 'achats'
  if (rubrique === 'banque') return 'releves'
  return 'autres'
}

export type EntiteClassement = { type: TypeEntite; cloture: string | null }

/**
 * Dossier de l'exercice : nommé par l'année de clôture (« Exercice 2026 »), ou par ses deux
 * années quand la clôture n'est pas au 31 décembre (« Exercice 2025-2026 »). L'exercice
 * concerné (liasse 2025 reçue en 2026) l'emporte sur la date du document. Foyer : « Année 2026 ».
 */
export function libelleExercice(
  entite: EntiteClassement,
  quand: { jour: string; exerciceConcerne?: number | null },
): string {
  const annee = Number(quand.jour.slice(0, 4))
  if (planDe(entite.type) === 'foyer') return `Année ${quand.exerciceConcerne ?? annee}`
  const cloture = entite.cloture ?? '12-31'
  const fin = quand.exerciceConcerne ?? (quand.jour.slice(5) <= cloture ? annee : annee + 1)
  return cloture === '12-31' ? `Exercice ${fin}` : `Exercice ${fin - 1}-${fin}`
}

/**
 * Premier jour (AAAA-MM-JJ) de l'exercice comptable qui contient `jour` : le lendemain de la
 * dernière clôture (MM-JJ) qui le précède ; sans clôture, l'année civile. Même découpage que
 * `libelleExercice`. Une clôture au 29 février tombe le 28 les années communes.
 */
export function debutExercice(cloture: string | null, jour: string): string {
  const annee = Number(jour.slice(0, 4))
  const c = cloture ?? '12-31'
  const fin = jour.slice(5) <= c ? annee : annee + 1
  return ajouterJours(jourDeCloture(fin - 1, c), 1)
}

/** Le jour de clôture (MM-JJ) d'une année, ramené au dernier jour de son mois. */
function jourDeCloture(annee: number, cloture: string): string {
  const mois = Number(cloture.slice(0, 2))
  const dernier = new Date(Date.UTC(annee, mois, 0)).getUTCDate()
  const j = Math.min(Number(cloture.slice(3, 5)), dernier)
  return `${annee}-${cloture.slice(0, 2)}-${String(j).padStart(2, '0')}`
}

/** La racine de classement est un chemin fixe, sans variable : elle doit exister dans le Drive. */
export function resoudreRacine(racine: string): string[] | { erreur: string } {
  if (racine.includes('{'))
    return { erreur: 'La racine de classement est un dossier fixe, sans variable.' }
  const r = resoudreChemin(racine, '2026-01-01')
  return 'erreur' in r ? r : r.existants.map((d) => d.trim())
}
